/**
 * useAssignees — hook condiviso che recupera la lista degli assegnatari
 * disponibili per un item di inventario.
 *
 * Restituisce sempre la stessa shape:
 *   { assignees: Array<{id, name, kind, role, store_id, store_name}>,
 *     loading: boolean }
 *
 * Refactor 2026-05-29 (idea_inventory_assignee_fix): in produzione gli
 * `users.role=store_user` sono quasi assenti, mentre ci sono ~110 staff
 * importati. Questo hook chiama il nuovo endpoint backend
 * `/api/inventory/assignees` che merge `staff_members` attivi + manager
 * (operations_manager / area_manager / hr_manager / store_manager).
 *
 * Usato da:
 *   - InventoryItemPage.js → EditItemDialog
 *   - InventoryPage.js → CreateItemDialog + BulkReassignDialog
 */
import { useEffect, useState } from 'react';
import { inventoryApi } from './api';


export function useAssignees() {
  const [assignees, setAssignees] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    inventoryApi.listAssignees()
      .then(res => { if (active) setAssignees(res.data || []); })
      .catch(() => { if (active) setAssignees([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  return { assignees, loading };
}


/**
 * Raggruppa la lista di assignees in:
 *   - sameStoreStaff (staff nello storeId corrente)
 *   - otherStaff (staff altri store)
 *   - managers (sempre cross-store)
 *
 * Helper standalone così è testabile e riusato dai 3 dialog senza duplicare.
 */
export function groupAssignees(assignees, currentStoreId) {
  const sameStoreStaff = [];
  const otherStaff = [];
  const managers = [];
  for (const a of assignees || []) {
    if (a.kind === 'manager') managers.push(a);
    else if (currentStoreId && a.store_id === currentStoreId) sameStoreStaff.push(a);
    else otherStaff.push(a);
  }
  return { sameStoreStaff, otherStaff, managers };
}
